<template>
  <a-modal
      :visible="visible"
      :width="'65%'"
      :style="{ height: '80%' }"
      :body-style="{ height: 'calc(100% - 110px)', padding: '16px', overflow: 'auto' }"
      :footer="null"
      :destroy-on-close="true"
      :mask-closable="true"
      :keyboard="true"
      title="汇总结算订单详情"
      @cancel="close"
  >
    <a-spin :spinning="loading">
      <!-- 顶部 alert:登记失败原因 -->
      <a-alert v-if="detail.summary && detail.summary.syncStatus === '2'" type="error" show-icon
               style="margin-bottom: 12px"
               message="该汇总结算订单银行登记失败"
               :description="detail.summary.syncFailMsg || '无具体原因'" />

      <!-- 汇总结算订单信息:column=3,只有"清算"4 字段用内嵌 column=2 子 Descriptions 实现两行两列 -->
      <a-descriptions :column="3" bordered size="small">
        <!-- 1. 基础信息 -->
        <a-descriptions-item label="汇总结算订单号">
          <span class="text-mono"><b>{{ detail.summary?.summaryNo || '----' }}</b></span>
        </a-descriptions-item>
        <a-descriptions-item label="合同号">{{ detail.summary?.contractNo || '----' }}</a-descriptions-item>
        <a-descriptions-item label="结算日期">{{ detail.summary?.settleDate || '----' }}</a-descriptions-item>

        <!-- 2. 商户/店铺信息 -->
        <a-descriptions-item label="商户号">{{ detail.summary?.mchNo || '----' }}</a-descriptions-item>
        <a-descriptions-item label="商户名">{{ detail.summary?.mchName || '----' }}</a-descriptions-item>
        <a-descriptions-item label="店铺号">{{ detail.summary?.shopNo || '----' }}</a-descriptions-item>
        <a-descriptions-item label="店铺名" :span="3">{{ detail.summary?.shopName || '----' }}</a-descriptions-item>

        <!-- 3. 核心金额 -->
        <a-descriptions-item label="需结算金额" :span="3">
          <Balance class="amount-emphasis-lg" :amount="detail.summary?.needSettleAmount || 0" dcflag="c" inline />
        </a-descriptions-item>

        <!-- 4. 匹配信息 -->
        <a-descriptions-item label="匹配状态">
          <a-tag :color="matchCfg(detail.summary?.matchStatus).color">
            {{ matchCfg(detail.summary?.matchStatus).label }}
          </a-tag>
        </a-descriptions-item>
        <a-descriptions-item label="匹配金额">
          <Balance class="amount-emphasis" :amount="detail.summary?.matchAmount || 0" dcflag="c" inline />
        </a-descriptions-item>
        <a-descriptions-item label="匹配时间">{{ detail.summary?.matchTime || '----' }}</a-descriptions-item>

        <!-- 5. 清算信息（三列平铺） -->
        <a-descriptions-item label="清算状态">
          <a-tag :color="settleCfg(detail.summary?.settleStatus).color">
            {{ settleCfg(detail.summary?.settleStatus).label }}
          </a-tag>
        </a-descriptions-item>
        <a-descriptions-item label="清算金额">
          <Balance class="amount-emphasis" :amount="detail.summary?.settleAmount || 0" dcflag="c" inline />
        </a-descriptions-item>
        <a-descriptions-item label="清算时间">{{ detail.summary?.settleTime || '----' }}</a-descriptions-item>
        <a-descriptions-item label="清算流水号" :span="3">
          <span class="text-mono">{{ detail.summary?.settleSerialNo || '----' }}</span>
        </a-descriptions-item>

        <!-- 6. 银行同步 -->
        <a-descriptions-item label="银行登记">
          <a-tag :color="syncCfg(detail.summary?.syncStatus).color">
            {{ syncCfg(detail.summary?.syncStatus).label }}
          </a-tag>
        </a-descriptions-item>
        <a-descriptions-item label="登记时间">{{ detail.summary?.syncTime || '----' }}</a-descriptions-item>
        <a-descriptions-item label="最后同步时间">{{ detail.summary?.lastSyncTime || '----' }}</a-descriptions-item>
      </a-descriptions>
      <!-- 详情信息:Tabs 标签页展示原始明细 + 银行来账匹配明细 -->
      <a-tabs v-model="activeTab" :animated="true" style="margin-top: 16px">
        <a-tab-pane key="items" :tab="`关联原始明细（${itemsTotal}）`">
          <!-- 筛选区 -->
          <div class="formbox">
            <div class="inputbox">
              <div class="lefttitle">订单号</div>
              <a-input v-model.trim="itemsQuery.orderNo" placeholder="订单号模糊" allowClear style="width: 180px"/>
            </div>
            <div class="inputbox">
              <div class="lefttitle">主订单号</div>
              <a-input v-model.trim="itemsQuery.mainOrderNo" placeholder="主订单号模糊" allowClear style="width: 180px"/>
            </div>
            <div class="inputbox">
              <div class="lefttitle">商品类型</div>
              <a-input v-model.trim="itemsQuery.goodsType" placeholder="商品类型(精确)" allowClear style="width: 140px"/>
            </div>
            <div class="inputbox">
              <div class="lefttitle">结算日期</div>
              <a-range-picker valueFormat="YYYY-MM-DD" v-model="itemsQuery.settleDateRange"
                              @change="onItemsDateChange" style="width: 220px"
                              :placeholder="['开始日期', '结束日期']"/>
            </div>
            <a-button @click="searchItems" type="primary">查询</a-button>
            <a-button @click="resetItemsQuery" style="margin-left: 8px">重置</a-button>
          </div>

          <a-table size="small" :columns="itemColumns" :data-source="itemsList"
                   :row-key="(r, i) => i" :pagination="itemsPagination"
                   :loading="itemsLoading" :scroll="{ x: 1100 }">
          </a-table>
        </a-tab-pane>
        <a-tab-pane key="match" :tab="`银行来账匹配明细（${(detail.matchDetails || []).length}）`">
          <a-table size="small" :columns="matchColumns" :data-source="detail.matchDetails || []"
                   :row-key="(r, i) => i" :pagination="false" :scroll="{ x: 1400 }">
          </a-table>
        </a-tab-pane>
      </a-tabs>
    </a-spin>

    <!-- 同步失败时,底部操作栏放置"重试推送"按钮 -->
    <template v-if="detail.summary && detail.summary.syncStatus === '2'" slot="footer">
      <a-button @click="close">关闭</a-button>
      <a-button type="primary" :loading="syncRetryLoading" @click="onRetrySync">重试推送</a-button>
    </template>
  </a-modal>
</template>

<script>
import Balance from '@/view/components/Balance.vue'
import {
  matchStatusConfig,
  settleStatusConfig,
  summarySyncStatusConfig,
} from '@/utils/bizDict/consignDict'

export default {
  name: 'ConsignSummaryDetail',
  components: { Balance },
  props: {
    visible: { type: Boolean, default: false },
    summaryNo: { type: String, default: '' },
  },
  data() {
    return {
      loading: false,
      syncRetryLoading: false,
      activeTab: 'items',
      detail: { summary: {}, matchDetails: [] },

      // items 分页独立状态(避免受 detail 接口影响)
      itemsList: [],
      itemsTotal: 0,
      itemsPageNo: 1,
      itemsPageSize: 20,
      itemsLoading: false,
      itemsQuery: {
        orderNo: '',
        mainOrderNo: '',
        goodsType: '',
        settleDateRange: [],
        settleDateStart: '',
        settleDateEnd: '',
      },

      matchColumns: [
        { title: '序号', dataIndex: 'index', width: 60, align: 'center',
          customRender: (t, r, i) => i + 1 },
        { title: '来账流水号', dataIndex: 'depositSerialNo', width: 200 },
        { title: '来账金额', dataIndex: 'depositAmount', width: 140,
          customRender: (t) => <Balance amount={t} dcflag="c" inline /> },
        { title: '本笔匹配金额', dataIndex: 'matchAmount', width: 140,
          customRender: (t) => <Balance amount={t} dcflag="c" inline /> },
        { title: '本方账户号', dataIndex: 'accountNo', width: 180 },
        { title: '本方账户名', dataIndex: 'accountName', width: 150 },
        { title: '对方账户号', dataIndex: 'oppAccountNo', width: 180 },
        { title: '对方账户名', dataIndex: 'oppAccountName', width: 150 },
        { title: '来账时间', dataIndex: 'depositTime', width: 160 },
        { title: '来账备注', dataIndex: 'depositRemark' },
      ],
    }
  },
  computed: {
    itemColumns() {
      const baseIndex = (this.itemsPageNo - 1) * this.itemsPageSize
      return [
        { title: '序号', width: 60, align: 'center',
          customRender: (t, r, i) => baseIndex + i + 1 },
        { title: '订单号', dataIndex: 'orderNo', width: 200 },
        { title: '主订单号', dataIndex: 'mainOrderNo', width: 180 },
        { title: '商品类型', dataIndex: 'goodsType', width: 100 },
        { title: '结算金额', dataIndex: 'settlementAmt', width: 140,
          customRender: (t) => <Balance amount={t} dcflag="c" inline /> },
        { title: '结算日期', dataIndex: 'settleDate', width: 110 },
        { title: '结算时间', dataIndex: 'settleTime', width: 160 },
      ]
    },
    itemsPagination() {
      return {
        current: this.itemsPageNo,
        pageSize: this.itemsPageSize,
        total: this.itemsTotal,
        showSizeChanger: true,
        showQuickJumper: true,
        pageSizeOptions: ['20', '50', '100', '200'],
        showTotal: (t) => `共 ${t} 条`,
        onChange: this.onItemsPageChange,
        onShowSizeChange: this.onItemsSizeChange,
      }
    },
  },
  watch: {
    visible(v) {
      if (v && this.summaryNo) {
        this.loadDetail()
        this.loadItems()
      }
    },
    summaryNo() {
      if (this.visible) {
        this.loadDetail()
        this.loadItems()
      }
    },
  },
  methods: {
    matchCfg(v) { return matchStatusConfig[v] || matchStatusConfig.default },
    settleCfg(v) { return settleStatusConfig[v] || settleStatusConfig.default },
    syncCfg(v) { return summarySyncStatusConfig[v] || summarySyncStatusConfig.default },
    close() {
      this.$emit('update:visible', false)
    },
    async loadDetail() {
      if (!this.summaryNo) {
        this.$message.error('汇总结算订单号缺失')
        return
      }
      this.loading = true
      try {
        const res = await this.api.summaryDetailApi(this.summaryNo)
        if (res.code !== 200) {
          this.$message.error(res.info || '查询失败')
          return
        }
        const data = res.data || {}
        this.detail = { summary: data.summary || {}, matchDetails: data.matchDetails || [] }
        // 切回第一个 tab(避免在 match tab 上打开新详情)
        this.activeTab = 'items'
      } finally {
        this.loading = false
      }
    },
    async loadItems() {
      if (!this.summaryNo) return
      this.itemsLoading = true
      try {
        const res = await this.api.summaryItemsPageApi({
          summaryNo: this.summaryNo,
          pageNo: this.itemsPageNo,
          pageSize: this.itemsPageSize,
          orderNo: this.itemsQuery.orderNo,
          mainOrderNo: this.itemsQuery.mainOrderNo,
          goodsType: this.itemsQuery.goodsType,
          settleDateStart: this.itemsQuery.settleDateStart,
          settleDateEnd: this.itemsQuery.settleDateEnd,
        })
        if (res.code !== 200) {
          this.$message.error(res.info || '原始明细查询失败')
          return
        }
        const page = res.data || {}
        this.itemsList = page.records || []
        this.itemsTotal = page.total || 0
      } finally {
        this.itemsLoading = false
      }
    },
    searchItems() {
      this.itemsPageNo = 1
      this.loadItems()
    },
    resetItemsQuery() {
      this.itemsQuery = {
        orderNo: '',
        mainOrderNo: '',
        goodsType: '',
        settleDateRange: [],
        settleDateStart: '',
        settleDateEnd: '',
      }
      this.itemsPageNo = 1
      this.loadItems()
    },
    onItemsDateChange(dates) {
      this.itemsQuery.settleDateStart = dates && dates[0] ? dates[0] : ''
      this.itemsQuery.settleDateEnd = dates && dates[1] ? dates[1] : ''
    },
    onItemsPageChange(page) {
      this.itemsPageNo = page
      this.loadItems()
    },
    onItemsSizeChange(current, size) {
      this.itemsPageNo = 1
      this.itemsPageSize = size
      this.loadItems()
    },
    async onRetrySync() {
      const summaryNo = this.detail.summary && this.detail.summary.summaryNo
      if (!summaryNo) return
      this.$confirm({
        title: '重试推送银行',
        content: `汇总结算订单号 ${summaryNo} 重新调 310907 银行登记,是否继续?`,
        okText: '确认',
        cancelText: '取消',
        onOk: async () => {
          this.syncRetryLoading = true
          try {
            const res = await this.api.summarySyncRetryApi({ summaryNo })
            if (res.code !== 200) {
              this.$message.error(res.info || '重试同步失败')
            } else {
              this.$message.success('已重新同步银行')
            }
            this.loadDetail()
          } finally {
            this.syncRetryLoading = false
          }
        },
      })
    },
  },
}
</script>

<style scoped>
.formbox {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  margin-bottom: 12px;
}
.formbox .inputbox {
  display: flex;
  align-items: center;
  margin-right: 12px;
  margin-bottom: 8px;
}
.formbox .lefttitle {
  width: 70px;
  text-align: right;
  margin-right: 8px;
  color: #666;
  font-size: 13px;
}
.text-mono {
  font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
  font-size: 12px;
}

/* 金额加粗(状态行内的匹配/清算金额,字号不变) */
.amount-emphasis .balance {
  font-weight: 700;
}

/* 需结算金额突出:整行 span=3,字号略大 + 加粗 */
.amount-emphasis-lg .balance {
  font-weight: 700;
  font-size: 18px;
}

/* 清算子 Descriptions:去掉外层 table 边框、底边距,适配嵌套 */
>>> .settle-nested.ant-descriptions {
  margin-bottom: 0;
}
>>> .settle-nested > table {
  border: none;
}
>>> .settle-nested .ant-descriptions-item-label {
  background: #fafafa;
  color: #666;
}
</style>